import { notFound } from '@tanstack/react-router';
import { AboutPage } from '~/app/about';
import { CareersPageContent } from '~/app/careers';
import { ComparePage } from '~/app/compare';
import { AccountsPageContent } from '~/app/index';
import { PerksPage } from '~/app/perks';
import { PricingPage } from '~/app/pricing';
import { CookiePolicyPage } from '~/app/terms.cookie';
import { ZakatCalculatorPage } from '~/app/zakat-calculator';
import { LearnArticleDetail } from '~/components/sections/LearnArticleDetail';
import { LegalDocument } from '~/components/sections/LegalDocument';
import { SendMoneyCalculatorPage } from '~/components/sections/SendMoneyCalculatorPage';
import { aboutMeta } from '~/content/about';
import {
  calculatorPairHeadMeta,
  getCalculatorPair,
  type CalculatorPairContent,
} from '~/content/calculatorPairs';
import { careersMeta, type CareerJob } from '~/content/careers';
import type { LearnArticle } from '~/content/learn';
import type { LegalPage } from '~/content/legal';
import type { FaqContent } from '~/content/types';
import { learnArticleHeadMeta } from '~/lib/learn-article-route';
import {
  isLocalizedCanonicalPath,
  isLocalizedLocale,
  normalizeCanonicalPath,
  type LocalizedLocale,
} from '~/lib/localized-routes';
import {
  getArticleByFullPath,
  getDefaultFaq,
  getJobs,
  getLegalPageByFullPath,
} from '~/lib/payload';
import { pageHeadMeta } from '~/lib/seo';
import heroCss from '~/styles/hero.css?inline';
import calculatorStylesHref from '~/styles/calculator.css?url';
import perksStylesHref from '~/styles/perks.css?url';
import pricingStylesHref from '~/styles/pricing.css?url';
import readingStylesHref from '~/styles/reading.css?url';
import zakatStylesHref from '~/styles/zakat.css?url';

type LocalizedPageData =
  | { kind: 'home'; locale: LocalizedLocale; faqContent: FaqContent }
  | { kind: 'about'; locale: LocalizedLocale }
  | { kind: 'article'; locale: LocalizedLocale; article: LearnArticle }
  | { kind: 'calculator'; locale: LocalizedLocale; pair: CalculatorPairContent }
  | { kind: 'careers'; locale: LocalizedLocale; jobs: CareerJob[] }
  | { kind: 'compare'; locale: LocalizedLocale }
  | { kind: 'perks'; locale: LocalizedLocale }
  | { kind: 'pricing'; locale: LocalizedLocale }
  | { kind: 'cookie'; locale: LocalizedLocale }
  | { kind: 'legal'; locale: LocalizedLocale; page: LegalPage }
  | { kind: 'zakat'; locale: LocalizedLocale };

function localizedNotFound(): never {
  throw notFound();
}

export async function loadLocalizedPage(
  localeParam: string,
  requestedCanonicalPath: string,
): Promise<LocalizedPageData> {
  const locale = localeParam.toLowerCase();
  const canonicalPath = normalizeCanonicalPath(requestedCanonicalPath);
  if (!isLocalizedLocale(locale) || !isLocalizedCanonicalPath(canonicalPath)) {
    return localizedNotFound();
  }

  if (canonicalPath === '/') {
    return { kind: 'home', locale, faqContent: await getDefaultFaq() };
  }
  if (canonicalPath === '/about') return { kind: 'about', locale };
  if (canonicalPath === '/careers') {
    return { kind: 'careers', locale, jobs: await getJobs() };
  }
  if (canonicalPath === '/compare') return { kind: 'compare', locale };
  if (canonicalPath === '/perks') return { kind: 'perks', locale };
  if (canonicalPath === '/pricing') return { kind: 'pricing', locale };
  if (canonicalPath === '/terms/cookie') return { kind: 'cookie', locale };
  if (canonicalPath === '/zakat-calculator') return { kind: 'zakat', locale };

  if (canonicalPath.startsWith('/calculator/')) {
    const pair = getCalculatorPair(canonicalPath.slice('/calculator/'.length));
    return pair ? { kind: 'calculator', locale, pair } : localizedNotFound();
  }

  if (
    canonicalPath.startsWith('/article/') ||
    canonicalPath.startsWith('/bangladesh/') ||
    canonicalPath.startsWith('/pakistan/') ||
    canonicalPath.startsWith('/syria/')
  ) {
    const article = await getArticleByFullPath({ data: canonicalPath.slice(1) });
    return article ? { kind: 'article', locale, article } : localizedNotFound();
  }

  if (canonicalPath.startsWith('/terms/')) {
    const page = await getLegalPageByFullPath({ data: canonicalPath.slice(1) });
    return page ? { kind: 'legal', locale, page } : localizedNotFound();
  }

  return localizedNotFound();
}

export function localizedPageHead(data: LocalizedPageData | undefined) {
  if (!data) return {};

  switch (data.kind) {
    case 'home':
      return pageHeadMeta({
        title: 'Open a Global USD Account and Send Money Today',
        description:
          'Open a global USD account with nsave. Get U.S. account and routing numbers to receive ACH payments and send money internationally with low fees.',
        path: '/',
        inlineStyles: [heroCss],
        locale: data.locale,
      });
    case 'about':
      return pageHeadMeta({
        title: aboutMeta.title,
        description: aboutMeta.description,
        path: '/about',
        locale: data.locale,
      });
    case 'article':
      return learnArticleHeadMeta(data.article, data.locale);
    case 'calculator': {
      const head = calculatorPairHeadMeta(data.pair, data.locale);
      return {
        ...head,
        links: [...head.links, { rel: 'stylesheet', href: calculatorStylesHref }],
      };
    }
    case 'careers':
      return pageHeadMeta({
        title: careersMeta.title,
        description: careersMeta.description,
        path: '/careers',
        locale: data.locale,
      });
    case 'compare':
      return pageHeadMeta({
        title: 'Compare nsave rates with other Alternatives',
        description: 'Compare nsave rates to other providers such as Wise, Payoneer, Xoom, OFX.',
        path: '/compare',
        locale: data.locale,
      });
    case 'perks':
      return pageHeadMeta({
        title: 'Perks — nsave',
        description:
          'Save with our global network. The nsave app comes with exclusive deals and offers from our partner network, all under Benefits.',
        path: '/perks',
        stylesheets: [perksStylesHref],
        locale: data.locale,
      });
    case 'pricing':
      return pageHeadMeta({
        title: 'Pricing – nsave',
        description:
          "Compare nsave's Free and Pro plans. Send money internationally, get USD/GBP accounts, and invest with transparent pricing. Pro plan just $9.99/month.",
        path: '/pricing',
        stylesheets: [pricingStylesHref],
        locale: data.locale,
      });
    case 'cookie':
      return pageHeadMeta({
        title: 'Cookie Policy – nsave',
        description:
          'How nsave uses cookies and similar technologies on the website, and how you can control them.',
        path: '/terms/cookie',
        stylesheets: [readingStylesHref],
        locale: data.locale,
      });
    case 'legal': {
      const title = data.page.metaTitle?.trim() || `${data.page.title} – nsave`;
      const description =
        data.page.metaDescription?.trim() || `${data.page.title} for nsave products and services.`;
      return pageHeadMeta({
        title,
        description,
        path: `/${data.page.fullPath}`,
        stylesheets: [readingStylesHref],
        locale: data.locale,
      });
    }
    case 'zakat':
      return pageHeadMeta({
        title: 'Calculate Your Zakat Today | Cash, Gold & Investments',
        description:
          "Calculate your Zakat with nsave's online calculator. Include cash, bank balances, gold, silver, and investments to see how much to pay today.",
        path: '/zakat-calculator',
        stylesheets: [zakatStylesHref],
        locale: data.locale,
      });
  }
}

export function LocalizedPage({ data }: { data: LocalizedPageData }) {
  switch (data.kind) {
    case 'home':
      return <AccountsPageContent faqContent={data.faqContent} />;
    case 'about':
      return <AboutPage />;
    case 'article':
      return <LearnArticleDetail key={data.article.fullPath} article={data.article} />;
    case 'calculator':
      return (
        <SendMoneyCalculatorPage
          key={data.pair.slug}
          heroTitle={data.pair.heroTitle}
          initialTarget={data.pair.target}
        />
      );
    case 'careers':
      return <CareersPageContent jobs={data.jobs} />;
    case 'compare':
      return <ComparePage />;
    case 'perks':
      return <PerksPage />;
    case 'pricing':
      return <PricingPage />;
    case 'cookie':
      return <CookiePolicyPage />;
    case 'legal':
      return <LegalDocument page={data.page} />;
    case 'zakat':
      return <ZakatCalculatorPage />;
  }
}
